import {
  CatalogCombustionEngine,
  CatalogCombustionEngineLabels,
  CatalogElectrificationLevel,
  CatalogElectrificationLevelLabels,
  CatalogFuel,
  CatalogFuelLabels,
  CatalogMotorSourceAxle,
  type VehicleCatalogNodeDetailRecord
} from '@navio/nsdk';
import { NavioPowerLabel, type NavioInfoCardItem } from 'noddi-ui-web';
import { catalogPowerText, formatMeasure, getLabelMapFromEntries, pluralize, shownChoice } from 'noddi-util';
import { catalogFactItem } from './catalogFactItem';

type Powertrain = NonNullable<VehicleCatalogNodeDetailRecord['powertrain']>;
const FUEL_LABEL = getLabelMapFromEntries(CatalogFuel, CatalogFuelLabels);
const ELECTRIFICATION_LABEL = getLabelMapFromEntries(CatalogElectrificationLevel, CatalogElectrificationLevelLabels);
const COMBUSTION_LABEL = getLabelMapFromEntries(CatalogCombustionEngine, CatalogCombustionEngineLabels);

const motorAxleLabel = (axle: Powertrain['motors'][number]['sourceAxle']): string | null => {
  if (axle === CatalogMotorSourceAxle.FRONT) {
    return 'Front';
  }
  if (axle === CatalogMotorSourceAxle.REAR) {
    return 'Rear';
  }
  return null;
};

const kilowatts = catalogPowerText;

const motorLine = (motor: Powertrain['motors'][number]): string | null => {
  const parts = [
    motorAxleLabel(motor.sourceAxle),
    motor.code,
    motor.peakPowerKw != null ? kilowatts(motor.peakPowerKw) : null
  ].filter((part) => part != null);
  return parts.length > 0 ? parts.join(' · ') : null;
};

export const catalogPowertrainItems = (powertrain: Powertrain): Array<NavioInfoCardItem | false> => {
  const headline = powertrain.headlinePowerKw;
  const engine = powertrain.enginePowerKw;
  const system = powertrain.systemPowerKw;
  const motors = powertrain.motors.map(motorLine).filter((line) => line != null);
  return [
    shownChoice(powertrain.primaryFuel) &&
      catalogFactItem('Fuel', FUEL_LABEL[powertrain.primaryFuel], { copyable: false }),
    shownChoice(powertrain.secondaryFuel) &&
      catalogFactItem('Secondary fuel', FUEL_LABEL[powertrain.secondaryFuel], { copyable: false }),
    shownChoice(powertrain.electrificationLevel) &&
      catalogFactItem('Electrification', ELECTRIFICATION_LABEL[powertrain.electrificationLevel], { copyable: false }),
    shownChoice(powertrain.combustionEngine) &&
      catalogFactItem('Combustion engine', COMBUSTION_LABEL[powertrain.combustionEngine], { copyable: false }),
    headline != null && powerFact('Power', headline),
    engine != null && engine !== headline && powerFact('Engine power', engine),
    system != null && system !== headline && system !== engine && powerFact('System power', system),
    powertrain.secondaryFuelEnginePowerKw != null &&
      powerFact('Secondary fuel power', powertrain.secondaryFuelEnginePowerKw),
    powertrain.engineDisplacementLitres != null &&
      catalogFactItem('Displacement', `${formatMeasure(powertrain.engineDisplacementLitres)} L`),
    catalogFactItem('Engine code', powertrain.engineCode ?? null),
    catalogFactItem('Engine layout', powertrain.engineLayoutLabel ?? null),
    motors.length > 0 &&
      catalogFactItem(pluralize(motors.length, 'Motor'), motors.join('; '), {
        content: (
          <div className='flex flex-col gap-1'>
            {powertrain.motors.map((motor) => (
              <span key={motor.id} className='flex flex-wrap items-center gap-1.5'>
                {[motorAxleLabel(motor.sourceAxle), motor.code].filter(Boolean).join(' · ')}
                {motor.peakPowerKw != null && <NavioPowerLabel kilowatts={motor.peakPowerKw} />}
              </span>
            ))}
          </div>
        )
      })
  ];
};

const powerFact = (label: string, kilowatts: number): NavioInfoCardItem | false =>
  catalogFactItem(label, catalogPowerText(kilowatts), { content: <NavioPowerLabel kilowatts={kilowatts} /> });
